import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import AuthContext from "../../contexts/AuthContext";
import { AppHeader } from "../../components/AppLayout";
import "../../components/AppLayout/AppLayout.css";
import "./Onboarding.css";

const ONBOARDING_SLIDES = [
  {
    icon: "tabler:password-user",
    title: "Enter Sponsor ID",
    description: "Enter the Sponsor ID shared by organiser to access your event",
  },
  {
    icon: "lucide:scan-line",
    title: "Capture Leads",
    description: "Start capturing leads by either scanning attendee QR code or entering their details",
  },
  {
    icon: "oui:export",
    title: "Export Leads",
    description: "Download leads details captured by you and your teammates at event",
  },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const Context = useContext(AuthContext);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px) to trigger slide change
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getNextRoute = () => {
    // If user has events, go to events page, otherwise go to enter sponsor ID page
    if (Context.eventUpdate && Context.eventUpdate.length > 0) {
      return "/events";
    }
    return "/no-event";
  };

  const handleNext = () => {
    if (currentSlide < ONBOARDING_SLIDES.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleFinish();
    }
  };

  const handleSkip = () => {
    navigate(getNextRoute());
  };

  const handleFinish = () => {
    navigate(getNextRoute());
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  const isLastSlide = currentSlide === ONBOARDING_SLIDES.length - 1;

  // Desktop Layout
  if (isDesktop) {
    return (
      <div className="onboarding-page">
        <AppHeader />

        {/* Main Content */}
        <main className="onboarding-desktop">
          <div className="onboarding-desktop__container">
            <div className="onboarding-desktop__cards">
              {ONBOARDING_SLIDES.map((slide, index) => (
                <React.Fragment key={index}>
                  <div className="onboarding-card">
                    <div className="onboarding-card__icon-wrapper">
                      <Icon icon={slide.icon} width="32" height="32" />
                    </div>
                    <h3 className="onboarding-card__title">{slide.title}</h3>
                    <p className="onboarding-card__description">{slide.description}</p>
                  </div>
                  {index < ONBOARDING_SLIDES.length - 1 && (
                    <div className="onboarding-desktop__connector" />
                  )}
                </React.Fragment>
              ))}
            </div>
            <button className="onboarding-desktop__next-btn" onClick={handleFinish}>
              Next
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Mobile Layout
  return (
    <div className="onboarding-page onboarding-page--mobile">
      <main className="onboarding-mobile">
        {/* Carousel */}
        <div
          className="onboarding-carousel"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="onboarding-carousel__track"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {ONBOARDING_SLIDES.map((slide, index) => (
              <div key={index} className="onboarding-slide">
                <div className="onboarding-slide__icon-bg">
                  <Icon icon={slide.icon} width="60" height="60" />
                </div>
                <h2 className="onboarding-slide__title">{slide.title}</h2>
                <p className="onboarding-slide__description">{slide.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="onboarding-dots">
          {ONBOARDING_SLIDES.map((_, index) => (
            <button
              key={index}
              className={`onboarding-dots__dot ${
                index === currentSlide ? "onboarding-dots__dot--active" : ""
              }`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="onboarding-nav">
          {!isLastSlide ? (
            <button className="onboarding-nav__skip" onClick={handleSkip}>
              Skip
            </button>
          ) : (
            <div />
          )}
          <button
            className="onboarding-nav__next"
            onClick={handleNext}
            aria-label={isLastSlide ? "Finish" : "Next slide"}
          >
            <Icon icon="quill:arrow-left" width="24" height="24" rotate={2} />
          </button>
        </div>
      </main>
    </div>
  );
};

export default Onboarding;
